// src/components/company/FarmerTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ref, onValue, off } from "firebase/database";
import { auth, firestore, database } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import PaymentModal from "./PaymentModal";
import EditFarmerModal from "./EditFarmerModal";
import RecordMilkModal from "./RecordMilkModal";
import AdvancePaymentModal from "./AdvancePaymentModal";
import FarmerViewModal from "./FarmerViewModal";
import { useDebounce } from "../../../utils/useDebounce";
import { Eye, Edit, Milk, DollarSign, TrendingUp } from "lucide-react";
import { DEFAULT_COST_PER_KG } from "../../../constants/farmerConstants";
import { maskPhoneNumber } from "../../../utils/dataMasking";
import { useIsCompanyAdmin } from "../../../utils/useUserRole";


/**
 * Calculate the total amount for a farmer (Balance - AdvanceBalance)
 * @param {number} balance - Farmer's current balance
 * @param {number} advanceBalance - Farmer's advance balance
 * @returns {number} - The net total
 */
const calculateFarmerTotal = (balance, advanceBalance) => {
  return Number(balance) - Number(advanceBalance || 0);
};


const FarmerTable = ({ companyName: companyNameProp, compact = false }) => {
  const [farmers, setFarmers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState(companyNameProp || "");
  const db = database;
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMilkModal, setShowMilkModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const isCompanyAdmin = useIsCompanyAdmin(companyName);

  // Debounce search term to reduce filtering on every keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Memoized filtered farmers based on debounced search term
  const filteredFarmers = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return farmers;
    }
    const lower = debouncedSearchTerm.toLowerCase();
    return farmers.filter(
      (f) =>
        f.name.toLowerCase().includes(lower) ||
        f.phone.toLowerCase().includes(lower) ||
        f.address.toLowerCase().includes(lower)
    );
  }, [debouncedSearchTerm, farmers]);


  // Detect logged-in user and reset company cache per user
  useEffect(() => {
    if (companyNameProp) {
      setCompanyName(companyNameProp);
      return undefined;
    }

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const cachedCompany = localStorage.getItem(`companyName_${user.uid}`);
        if (cachedCompany) {
          setCompanyName(cachedCompany);
        } else {
          // Fetch from Firestore
          try {
            const docSnap = await getDoc(doc(firestore, "users", user.uid));
            if (docSnap.exists()) {
              const data = docSnap.data();
              if (data.companyName) {
                setCompanyName(data.companyName);
                localStorage.setItem(`companyName_${user.uid}`, data.companyName);
              }
            }
          } catch (err) {
            console.error("Error fetching company name:", err);
            toast.error("Failed to fetch company name");
          }
        }
      } else {
        // Clear everything if logged out
        setCompanyName("");
        setFarmers([]);
      }
    });

    return () => unsubscribe();
  }, [companyNameProp]);

  // Fetch farmers for the company in real-time
  useEffect(() => {
    if (!companyName || !db) return;

    const farmersRef = ref(db, `Companies/${companyName}/ValidatedFarmers`);
    setLoading(true);

    onValue(
      farmersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const farmerList = Object.entries(data).map(([key, value]) => ({
            id: key,
            name: value.CloudName || "Unnamed Farmer",
            otherName: value.OtherName || "N/A",
            address: value.Address || "N/A",
            phone: value.PhoneNumber || "N/A",
            idNumber: value.IdentificationNumber || "",
            hat: value.Hat || "N/A",
            balance: value.Balance || 0,
            mode: value.Mode || "",
            status: value.Status || "Active",
            createdAt: value.CreatedAt || "",
            advanceBalance: parseFloat(value.AdvanceBalance || 0),
            costPerKg: parseFloat(value.CostPerKg) > 0 ? parseFloat(value.CostPerKg) : null,
          }));
          setFarmers(farmerList);
        } else {
          setFarmers([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading farmers:", error);
        toast.error("Failed to load farmers");
        setLoading(false);
      }
    );

    return () => {
      off(farmersRef);
    };
  }, [companyName, db]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleEditClick = useCallback((farmer) => {
    setSelectedFarmer(farmer);
    setShowEditModal(true);
  }, []);

  const handleMilkClick = useCallback((farmer) => {
    setSelectedFarmer(farmer);
    setShowMilkModal(true);
  }, []);

  const handlePayClick = useCallback((farmer) => {
    setSelectedFarmer(farmer);
    setShowPaymentModal(true);
  }, []);

  const handleAdvanceClick = useCallback((farmer) => {
    setSelectedFarmer(farmer);
    setShowAdvanceModal(true);
  }, []);

  const handleViewClick = useCallback((farmer) => {
    setSelectedFarmer(farmer);
    setShowViewModal(true);
  }, []);



  return (
    <div className="overflow-x-auto overflow-y-auto mt-4 max-h-[65vh] rounded-lg border border-gray-100 shadow-sm bg-white">
      {/* 🔍 Search bar */}
      {!compact && (
        <div className="flex justify-between items-center mb-3">
          <input
            type="text"
            placeholder="Search farmer by name, phone, or Address..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="border px-3 py-2 rounded-lg w-80 focus:outline-none focus:ring-2 focus:ring-cyan-600"
          />
        </div>
      )}

      {/* Table */}
      <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <thead className="bg-cyan-600 text-white">
          <tr>
            <th className="text-left py-3 px-4 rounded-tl-lg">Farmer Name</th>
            {!compact && <th className="text-left py-3 px-4">Phone</th>}
            {!compact && <th className="text-left py-3 px-4">Address</th>}
            {!compact && <th className="text-left py-3 px-4">Balance (KES)</th>}
            {!compact && <th className="text-left py-3 px-4">Adv. Balance (KES)</th>}
            {!compact && <th className="text-left py-3 px-4">Total (KES)</th>}
            {!compact && <th className="text-left py-3 px-4">Mode</th>}
            <th className="text-center py-3 px-4 rounded-tr-lg">Actions</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={compact ? 2 : 8}
                className="text-center py-6 text-gray-500 italic"
              >
                Loading farmers...
              </td>
            </tr>
          ) : filteredFarmers.length === 0 ? (
            <tr>
              <td
                colSpan={compact ? 2 : 8}
                className="text-center py-6 text-gray-500 italic"
              >
                No farmers found.
              </td>
            </tr>
          ) : (
            filteredFarmers.map((farmer, index) => {
              const total = calculateFarmerTotal(farmer.balance, farmer.advanceBalance);
              return (
              <tr
                key={farmer.id}
                className={`border-t hover:bg-cyan-50 transition ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <td className="py-3 px-4 font-medium text-gray-800">
                  {farmer.name}
                </td>
                {!compact && <td className="py-3 px-4 text-gray-600">{isCompanyAdmin ? farmer.phone : maskPhoneNumber(farmer.phone)}</td>}
                {!compact && <td className="py-3 px-4 text-gray-600">{farmer.address}</td>}
                {!compact && (
                  <td className="py-3 px-4 text-gray-700">
                    {Number(farmer.balance).toLocaleString()}
                  </td>
                )}
                {!compact && (
                  <td className="py-3 px-4 text-gray-700">
                    {Number(farmer.advanceBalance || 0).toLocaleString()}
                  </td>
                )}
                {!compact && (
                  <td className={`py-3 px-4 font-semibold ${
                    total >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                    {total.toLocaleString()}
                  </td>
                )}
                {!compact && <td className="py-3 px-4 text-gray-600">{farmer.mode}</td>}
                <td className="py-3 px-4 text-center space-x-2">
                  <button
                    className="bg-cyan-600 text-white p-2 rounded-md hover:bg-cyan-700 inline-flex items-center justify-center"
                    onClick={() => handleViewClick(farmer)}
                    title="View Details"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className="bg-amber-500 text-white p-2 rounded-md hover:bg-amber-600 inline-flex items-center justify-center"
                    onClick={() => handleEditClick(farmer)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700 inline-flex items-center justify-center"
                    onClick={() => handleMilkClick(farmer)}
                    title="Record Milk"
                  >
                    <Milk size={16} />
                  </button>
                  <button
                    className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 inline-flex items-center justify-center"
                    onClick={() => handlePayClick(farmer)}
                    title="Pay"
                  >
                    <DollarSign size={16} />
                  </button>
                  <button
                    className="bg-purple-600 text-white p-2 rounded-md hover:bg-purple-700 inline-flex items-center justify-center"
                    onClick={() => handleAdvanceClick(farmer)}
                    title="Manage Advance"
                  >
                    <TrendingUp size={16} />
                  </button>
                </td>
              </tr>
            );
            })
          )}
        </tbody>
      </table>

      {/* Summary card */}
      {!compact && (
        <div className="mt-6 p-4 bg-cyan-50 border border-cyan-100 rounded-lg">
          <p className="text-sm text-cyan-800">
            💡 Tip: Use the search bar to quickly find farmers by name or hat.
            Click “Edit” to modify farmer details or “Record Milk” to log daily
            collection.
          </p>
        </div>
      )}
      {showPaymentModal && (
            <PaymentModal
              farmer={selectedFarmer}
              companyName={companyName}
              onClose={() => setShowPaymentModal(false)}
            />
          )}
      {showEditModal && selectedFarmer && (
            <EditFarmerModal
              farmer={selectedFarmer}
              companyName={companyName}
              onClose={() => setShowEditModal(false)}
              // Todo: refresh list
              ///onSave={fetchFarmers} // optional: refresh list
            />
          )}
      {showMilkModal && selectedFarmer && (
            <RecordMilkModal
              farmer={selectedFarmer}
              companyName={companyName}
              onClose={() => setShowMilkModal(false)}
            />
          )}
      {showAdvanceModal && selectedFarmer && (
            <AdvancePaymentModal
              farmer={selectedFarmer}
              companyName={companyName}
              onClose={() => setShowAdvanceModal(false)}
            />
          )}
      {showViewModal && selectedFarmer && (
            <FarmerViewModal
              farmer={selectedFarmer}
              companyName={companyName}
              onClose={() => setShowViewModal(false)}
            />
          )}

    </div>
  );
};



export default FarmerTable;
